import { appConfirm, appPrompt } from '../../services/appDialogs';
import { AppSelect } from '../ui/AppSelect';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Background,
  Controls,
  MarkerType,
  ReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  CircleDot,
  Eye,
  FileText,
  Flag,
  GitBranch,
  LocateFixed,
  MapPin,
  MessageSquareText,
  Network,
  PanelLeft,
  Play,
  Plus,
  Save,
  Search,
  Settings2,
  Trash2,
  UserPlus,
  Users,
  Variable,
  Workflow,
  X,
} from "lucide-react";
import { Button, IconButton } from "../ui/Button";
import { useToast } from "../../context/ToastContext";
import {
  CONDITION_OPERATORS,
  IMPACT_TYPE_OPTIONS,
  STORY_SCHEMA_VERSION,
  STORY_TRACK_TYPES,
  STORY_TRACK_TYPE_OPTIONS,
  createStoryAnswer,
  createStoryCharacter,
  createStoryNode,
  deriveStoryEdges,
  generateStoryId,
  normalizeStoryCondition,
  normalizeStoryEffect,
  normalizeStoryState,
  readStoryAnswerIdFromHandle,
} from "./storyModel";
import { layoutStoryGraph } from "./storyLayout";
import {
  fetchInteractiveStoryState,
  saveInteractiveStoryState,
} from "../../services/interactiveStoryService";
import StoryPlotNode from "./StoryPlotNode";
import StoryTrackEdge from "./StoryTrackEdge";
import StoryPreviewModal from "./StoryPreviewModal";
import "./InteractiveStoryWorkbench.css";

const NODE_TYPES = { storyPlot: StoryPlotNode };
const EDGE_TYPES = { storyTrack: StoryTrackEdge };

const ENDING_OPTIONS = [
  { value: "true", label: "真结局" },
  { value: "good", label: "好结局" },
  { value: "normal", label: "普通结局" },
  { value: "bad", label: "坏结局" },
  { value: "hidden", label: "隐藏结局" },
];

const EFFECT_SCOPE_OPTIONS = [
  { value: "relation", label: "人物关系" },
  { value: "stage", label: "人物阶段" },
  { value: "flag", label: "剧情标记" },
];

const ensureWorkspaceState = (value, projectId) => {
  const normalized = normalizeStoryState(value, projectId);
  const chapters = normalized.chapters.length
    ? normalized.chapters
    : [{ id: generateStoryId("chapter"), title: "第一章", order: 0 }];
  const defaultChapterId = chapters[0].id;
  const chapterIds = new Set(chapters.map((chapter) => chapter.id));
  const nodeIds = new Set(normalized.nodes.map((node) => node.id));
  return {
    ...normalized,
    chapters,
    nodes: normalized.nodes.map((node) => ({
      ...node,
      chapterId: node.chapterId && chapterIds.has(node.chapterId) ? node.chapterId : defaultChapterId,
      answers: node.answers.map((answer) => ({
        ...answer,
        targetNodeId: answer.targetNodeId && nodeIds.has(answer.targetNodeId)
          ? answer.targetNodeId
          : null,
      })),
    })),
  };
};

const needsWorkspaceMigration = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return true;
  if (value.schemaVersion !== STORY_SCHEMA_VERSION) return true;
  const nodes = Array.isArray(value.nodes) ? value.nodes : [];
  const chapters = Array.isArray(value.chapters) ? value.chapters : [];
  const characters = Array.isArray(value.characters) ? value.characters : [];
  const variables = Array.isArray(value.variables) ? value.variables : [];
  if (!nodes.length || !chapters.length || !value.initialState
    || !value.initialState.relations || !value.initialState.flags || !value.initialState.stages) return true;

  const nodeIds = new Set(nodes.map((node) => String(node?.id || "")).filter(Boolean));
  const chapterIds = new Set(chapters.map((chapter) => String(chapter?.id || "")).filter(Boolean));
  if (!value.entryNodeId || !nodeIds.has(String(value.entryNodeId))) return true;
  if (chapters.some((chapter) => !chapter?.id)) return true;
  if (characters.some((character) => !character?.id)) return true;
  if (variables.some((variable) => !variable?.id || !variable?.key
    || !Object.prototype.hasOwnProperty.call(variable, "defaultValue"))) return true;

  return nodes.some((node) => {
    if (!node?.id || !chapterIds.has(String(node.chapterId || ""))) return true;
    if (!STORY_TRACK_TYPES[node.trackType]) return true;
    if (Object.prototype.hasOwnProperty.call(node, "body")
      || Object.prototype.hasOwnProperty.call(node, "location")
      || Object.prototype.hasOwnProperty.call(node, "endingTitle")
      || Object.prototype.hasOwnProperty.call(node, "routeType")
      || Object.prototype.hasOwnProperty.call(node, "plotRole")
      || Object.prototype.hasOwnProperty.call(node, "lineType")) return true;
    return (Array.isArray(node.answers) ? node.answers : []).some((answer) => (
      !answer?.id
      || Object.prototype.hasOwnProperty.call(answer, "label")
      || Object.prototype.hasOwnProperty.call(answer, "feedback")
      || Object.prototype.hasOwnProperty.call(answer, "targetId")
      || (answer.targetNodeId && !nodeIds.has(String(answer.targetNodeId)))
    ));
  });
};

const Field = ({ label, hint, children, className = "", as: Component = "label" }) => (
  <Component className={`interactive-field ${className}`}>
    <span className="interactive-field__label">{label}</span>
    {children}
    {hint ? <small>{hint}</small> : null}
  </Component>
);

const InspectorSection = ({ title, icon: Icon, description, actions, children, className = "" }) => (
  <section className={`interactive-inspector-section ${className}`}>
    <header className="interactive-inspector-section__header">
      <div>
        <strong>{Icon ? <Icon size={14} /> : null}{title}</strong>
        {description ? <p>{description}</p> : null}
      </div>
      {actions}
    </header>
    {children}
  </section>
);

const StateKeyInput = ({ scope, value, characters, variables, onChange }) => {
  if (scope === "relation" || scope === "stage") {
    return (
      <AppSelect value={value || ""} onChange={(event) => onChange(event.target.value)}>
        <option value="">选择人物</option>
        {characters.map((character) => (
          <option key={character.id} value={character.id}>{character.name}</option>
        ))}
      </AppSelect>
    );
  }

  return (
    <AppSelect value={value || ""} onChange={(event) => onChange(event.target.value)}>
      <option value="">选择剧情标记</option>
      {variables.map((variable) => <option key={variable.id || variable.key} value={variable.key}>{variable.name || variable.key}</option>)}
    </AppSelect>
  );
};

function ConditionEditor({ condition, characters, variables, onChange, onRemove }) {
  const update = (patch) => onChange({ ...condition, ...patch });
  const needsValue = !["truthy", "falsy"].includes(condition.operator);
  const operatorOptions = condition.scope === "relation"
    ? CONDITION_OPERATORS.filter((item) => !["truthy", "falsy", "in", "notIn"].includes(item.value))
    : condition.scope === "flag"
      ? CONDITION_OPERATORS.filter((item) => ["eq", "neq", "truthy", "falsy"].includes(item.value))
      : CONDITION_OPERATORS.filter((item) => ["eq", "neq"].includes(item.value));

  return (
    <div className="interactive-rule-row">
      <div className="interactive-rule-row__topline">
        <span>仅当</span>
        <IconButton type="button" size="xs" label="删除出现条件" onClick={onRemove}><X size={13} /></IconButton>
      </div>
      <div className="interactive-rule-grid">
        <AppSelect
          aria-label="条件类型"
          value={condition.scope}
          onChange={(event) => update({
            scope: event.target.value,
            key: "",
            operator: event.target.value === "flag" ? "truthy" : "eq",
            value: event.target.value === "relation" ? 0 : event.target.value === "flag" ? true : "",
          })}
        >
          {EFFECT_SCOPE_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
        </AppSelect>
        <StateKeyInput
          scope={condition.scope}
          value={condition.key}
          characters={characters}
          variables={variables}
          onChange={(key) => update({ key })}
        />
        <AppSelect aria-label="条件运算" value={condition.operator} onChange={(event) => update({ operator: event.target.value })}>
          {operatorOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
        </AppSelect>
        {needsValue ? condition.scope === "flag" ? (
          <AppSelect aria-label="条件值" value={String(condition.value)} onChange={(event) => update({ value: event.target.value === "true" })}>
            <option value="true">是</option>
            <option value="false">否</option>
          </AppSelect>
        ) : (
          <input
            aria-label="条件值"
            type={condition.scope === "relation" ? "number" : "text"}
            value={condition.value ?? ""}
            placeholder={condition.scope === "relation" ? "0" : "阶段名称"}
            onChange={(event) => update({
              value: condition.scope === "relation" ? Number(event.target.value) : event.target.value,
            })}
          />
        ) : <span className="interactive-rule-row__implicit">无需填写值</span>}
      </div>
    </div>
  );
}

function EffectEditor({ effect, characters, variables, onChange, onRemove }) {
  const update = (patch) => onChange({ ...effect, ...patch });
  const relationOperations = [
    { value: "add", label: "增加" },
    { value: "subtract", label: "减少" },
    { value: "set", label: "设为" },
  ];

  return (
    <div className="interactive-rule-row is-effect">
      <div className="interactive-rule-row__topline">
        <span>选择后</span>
        <IconButton type="button" size="xs" label="删除状态变化" onClick={onRemove}><X size={13} /></IconButton>
      </div>
      <div className="interactive-rule-grid">
        <AppSelect
          aria-label="变化类型"
          value={effect.scope}
          onChange={(event) => update({
            scope: event.target.value,
            key: "",
            operation: event.target.value === "relation" ? "add" : "set",
            value: event.target.value === "relation" ? 1 : event.target.value === "flag" ? true : "",
          })}
        >
          {EFFECT_SCOPE_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
        </AppSelect>
        <StateKeyInput
          scope={effect.scope}
          value={effect.key}
          characters={characters}
          variables={variables}
          onChange={(key) => update({ key })}
        />
        {effect.scope === "relation" ? (
          <AppSelect aria-label="变化方式" value={effect.operation} onChange={(event) => update({ operation: event.target.value })}>
            {relationOperations.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </AppSelect>
        ) : <span className="interactive-rule-row__implicit">设为</span>}
        {effect.scope === "flag" ? (
          <AppSelect aria-label="变化值" value={String(effect.value)} onChange={(event) => update({ value: event.target.value === "true" })}>
            <option value="true">成立</option>
            <option value="false">不成立</option>
          </AppSelect>
        ) : (
          <input
            aria-label="变化值"
            type={effect.scope === "relation" ? "number" : "text"}
            value={effect.value ?? ""}
            placeholder={effect.scope === "relation" ? "1" : "例如：开始信任"}
            onChange={(event) => update({
              value: effect.scope === "relation" ? Number(event.target.value) : event.target.value,
            })}
          />
        )}
      </div>
    </div>
  );
}

function AnswerEditor({
  answer,
  index,
  expanded,
  nodes,
  characters,
  variables,
  onToggle,
  onChange,
  onRemove,
  onContinue,
  onLocateTarget,
}) {
  const impact = IMPACT_TYPE_OPTIONS.find((item) => item.value === answer.impactType) || IMPACT_TYPE_OPTIONS[2];
  const updateCondition = (conditionId, nextCondition) => onChange({
    ...answer,
    conditions: answer.conditions.map((item) => item.id === conditionId ? nextCondition : item),
  });
  const updateEffect = (effectId, nextEffect) => onChange({
    ...answer,
    effects: answer.effects.map((item) => item.id === effectId ? nextEffect : item),
  });

  return (
    <article className={`interactive-answer-editor ${expanded ? "is-expanded" : ""}`}>
      <button type="button" className="interactive-answer-editor__summary" onClick={onToggle}>
        <span className="interactive-answer-editor__number">{index + 1}</span>
        <span className="interactive-answer-editor__summary-copy">
          <strong>{answer.text || `回答 ${index + 1}`}</strong>
          <small style={{ color: impact.color }}>{impact.label}{answer.targetNodeId ? " · 已连接" : " · 未续写"}</small>
        </span>
        {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
      </button>

      {expanded ? (
        <div className="interactive-answer-editor__body">
          <Field label="玩家回答">
            <textarea rows={2} value={answer.text} placeholder="玩家会看到并点击的回答" onChange={(event) => onChange({ ...answer, text: event.target.value })} />
          </Field>

          <div className="interactive-two-column">
            <Field label="影响范围">
              <AppSelect value={answer.impactType} onChange={(event) => onChange({ ...answer, impactType: event.target.value })}>
                {IMPACT_TYPE_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
              </AppSelect>
            </Field>
            {answer.impactType === "character" ? (
              <Field label="主要影响谁">
                <AppSelect value={answer.focusCharacterId || ""} onChange={(event) => onChange({ ...answer, focusCharacterId: event.target.value || null })}>
                  <option value="">选择人物</option>
                  {characters.map((character) => <option key={character.id} value={character.id}>{character.name}</option>)}
                </AppSelect>
              </Field>
            ) : null}
          </div>

          <Field label="即时反馈" hint="做出选择后、进入下一段剧情前展示的一句话。">
            <input value={answer.immediateFeedback || ""} placeholder="例如：她明显松了一口气。" onChange={(event) => onChange({ ...answer, immediateFeedback: event.target.value })} />
          </Field>

          <div className="interactive-answer-destination">
            <Field label="后续剧情">
              <AppSelect value={answer.targetNodeId || ""} onChange={(event) => onChange({ ...answer, targetNodeId: event.target.value || null })}>
                <option value="">尚未连接</option>
                {nodes.map((node) => <option key={node.id} value={node.id}>{node.title || "未命名剧情"}</option>)}
              </AppSelect>
            </Field>
            <div className="interactive-answer-destination__actions">
              <Button type="button" size="sm" variant="secondary" onClick={onContinue}>
                {answer.targetNodeId ? <LocateFixed size={14} /> : <Plus size={14} />}
                {answer.targetNodeId ? "定位后续" : "续写这一支"}
              </Button>
              {answer.targetNodeId ? (
                <IconButton type="button" size="sm" label="在画布中定位后续剧情" onClick={onLocateTarget}><Eye size={14} /></IconButton>
              ) : null}
            </div>
          </div>

          <div className="interactive-answer-rules">
            <div className="interactive-answer-rules__heading">
              <div><strong>出现条件</strong><small>不满足时，玩家看不到这个回答。</small></div>
              <Button type="button" size="xs" variant="ghost" onClick={() => onChange({
                ...answer,
                conditions: [...answer.conditions, normalizeStoryCondition({ scope: "flag", key: "", operator: "truthy", value: true })],
              })}><Plus size={12} /> 条件</Button>
            </div>
            {answer.conditions.map((condition) => (
              <ConditionEditor
                key={condition.id}
                condition={condition}
                characters={characters}
                variables={variables}
                onChange={(next) => updateCondition(condition.id, next)}
                onRemove={() => onChange({ ...answer, conditions: answer.conditions.filter((item) => item.id !== condition.id) })}
              />
            ))}
            {!answer.conditions.length ? <p className="interactive-rule-empty">任何状态下都可以选择</p> : null}
          </div>

          <div className="interactive-answer-rules">
            <div className="interactive-answer-rules__heading">
              <div><strong>状态变化</strong><small>记录关系、人物阶段或剧情事实。</small></div>
              <Button type="button" size="xs" variant="ghost" onClick={() => onChange({
                ...answer,
                effects: [...answer.effects, normalizeStoryEffect({ scope: "relation", key: "", operation: "add", value: 1 })],
              })}><Plus size={12} /> 变化</Button>
            </div>
            {answer.effects.map((effect) => (
              <EffectEditor
                key={effect.id}
                effect={effect}
                characters={characters}
                variables={variables}
                onChange={(next) => updateEffect(effect.id, next)}
                onRemove={() => onChange({ ...answer, effects: answer.effects.filter((item) => item.id !== effect.id) })}
              />
            ))}
            {!answer.effects.length ? <p className="interactive-rule-empty">这个选择暂时不改变长期状态</p> : null}
          </div>

          <Button type="button" size="sm" variant="ghost" className="interactive-danger-link" onClick={onRemove}>
            <Trash2 size={14} /> 删除这个回答
          </Button>
        </div>
      ) : null}
    </article>
  );
}

function NodeInspector({
  node,
  story,
  onUpdate,
  onDelete,
  onSetEntry,
  onChangeType,
  onUpdateAnswer,
  onAddAnswer,
  onRemoveAnswer,
  onContinueAnswer,
  onLocateNode,
  onSelectCharacter,
  onPreview,
  onClose,
}) {
  const [expandedAnswerId, setExpandedAnswerId] = useState(node.answers?.[0]?.id || null);
  const track = STORY_TRACK_TYPES[node.trackType] || STORY_TRACK_TYPES.main;

  useEffect(() => {
    setExpandedAnswerId(node.answers?.[0]?.id || null);
  }, [node.id]);

  const participantProfiles = (node.participants || [])
    .map((id) => story.characters.find((character) => character.id === id))
    .filter(Boolean);

  return (
    <>
      <div className="interactive-inspector__titlebar">
        <div>
          <span>{track.label} · {node.nodeType === "ending" ? "结局节点" : "剧情节点"}</span>
          <h2>{node.title || "未命名剧情"}</h2>
        </div>
        <div className="interactive-inspector__title-actions">
          <IconButton type="button" size="sm" label="从此节点预演（使用开场初始状态）" onClick={onPreview}><Play size={15} /></IconButton>
          {story.entryNodeId !== node.id ? (
            <IconButton type="button" size="sm" label="设为故事开场" onClick={onSetEntry}><CircleDot size={15} /></IconButton>
          ) : null}
          <IconButton type="button" size="sm" label="删除节点" className="interactive-icon-danger" onClick={onDelete}><Trash2 size={15} /></IconButton>
          <IconButton type="button" size="sm" label="关闭编辑器" onClick={onClose}><X size={16} /></IconButton>
        </div>
      </div>

      <div className="interactive-inspector__scroll">
        <InspectorSection title="这段剧情" icon={FileText}>
          <div className="interactive-two-column">
            <Field label="节点类型">
              <AppSelect value={node.nodeType} onChange={(event) => onChangeType(event.target.value)}>
                <option value="story">剧情</option>
                <option value="ending">结局</option>
              </AppSelect>
            </Field>
            <Field label="所属章节">
              <AppSelect value={node.chapterId || ""} onChange={(event) => onUpdate({ chapterId: event.target.value || null })}>
                {story.chapters.map((chapter) => <option key={chapter.id} value={chapter.id}>{chapter.title}</option>)}
              </AppSelect>
            </Field>
          </div>
          <Field
            label="剧情线路"
            hint="线路决定节点和进入该节点的连线颜色；它与回答的影响程度互不替代。"
            as="div"
          >
            <div className="interactive-track-picker" role="radiogroup" aria-label="剧情线路">
              {STORY_TRACK_TYPE_OPTIONS.map((item) => (
                <button
                  type="button"
                  role="radio"
                  aria-checked={node.trackType === item.value}
                  key={item.value}
                  className={node.trackType === item.value ? "is-active" : ""}
                  style={{ "--story-track-color": item.color }}
                  onClick={() => onUpdate({ trackType: item.value })}
                >
                  <i aria-hidden="true" />
                  <span><strong>{item.label}</strong><small>{item.description}</small></span>
                </button>
              ))}
            </div>
          </Field>
          <Field label={node.nodeType === "ending" ? "结局名称" : "剧情标题"}>
            <input value={node.title} placeholder="给这一段一个容易辨认的名字" onChange={(event) => onUpdate({ title: event.target.value })} />
          </Field>
          <Field label="场景 / 地点">
            <div className="interactive-input-with-icon"><MapPin size={14} /><input value={node.scene || ""} placeholder="例如：深夜，公寓客厅" onChange={(event) => onUpdate({ scene: event.target.value })} /></div>
          </Field>
          <Field label="出场人物" as="div">
            <div className="interactive-character-checks">
              {story.characters.map((character) => {
                const checked = node.participants.includes(character.id);
                return (
                  <label key={character.id} className={checked ? "is-checked" : ""}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => onUpdate({
                        participants: checked
                          ? node.participants.filter((id) => id !== character.id)
                          : [...node.participants, character.id],
                      })}
                    />
                    <span style={{ backgroundColor: character.color }} />
                    {character.name}
                  </label>
                );
              })}
              {!story.characters.length ? <p className="interactive-rule-empty">先在左侧“人物”中建立角色</p> : null}
            </div>
          </Field>
          <Field
            label="剧情正文"
            hint="写玩家在上一次选择之后实际读到的连续剧情。这里不用按时长或镜头拆分。"
          >
            <textarea
              className="interactive-story-prose-input"
              rows={12}
              value={node.content || ""}
              placeholder="人物做了什么、说了什么、发现了什么……"
              onChange={(event) => onUpdate({ content: event.target.value })}
            />
          </Field>
        </InspectorSection>

        {participantProfiles.length ? (
          <InspectorSection title="人物对照" icon={Users} description="写作时随时核对这一场人物的目标、性格和秘密。">
            <div className="interactive-profile-strip">
              {participantProfiles.map((character) => (
                <button type="button" key={character.id} onClick={() => onSelectCharacter(character.id)}>
                  <span className="interactive-profile-strip__avatar" style={{ backgroundColor: character.color }}>{character.name.slice(0, 1)}</span>
                  <span><strong>{character.name}</strong><small>{character.tagline || character.goal || "还没有人物简介"}</small></span>
                  <ChevronRight size={14} />
                </button>
              ))}
            </div>
          </InspectorSection>
        ) : null}

        {node.nodeType === "ending" ? (
          <InspectorSection title="结局定义" icon={Flag} description="结局是剧情链的终点，不再抛出问题。">
            <Field label="结局级别">
              <AppSelect value={node.endingType || "normal"} onChange={(event) => {
                const endingType = event.target.value;
                onUpdate({
                  endingType,
                  ...(endingType === "bad" && node.trackType === "ending_check" ? { trackType: "lock_be" } : {}),
                });
              }}>
                {ENDING_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
              </AppSelect>
            </Field>
          </InspectorSection>
        ) : (
          <InspectorSection
            title="问题与回答"
            icon={GitBranch}
            description="这段剧情结束时抛出一个问题，每个回答通往下一段剧情。"
            actions={<Button type="button" size="xs" variant="secondary" onClick={() => {
              const answer = onAddAnswer();
              if (answer?.id) setExpandedAnswerId(answer.id);
            }}><Plus size={12} /> 回答</Button>}
          >
            <Field label="抛给玩家的问题">
              <textarea rows={3} value={node.question || ""} placeholder="例如：你准备相信谁？" onChange={(event) => onUpdate({ question: event.target.value })} />
            </Field>
            <div className="interactive-answer-editor-list">
              {node.answers.map((answer, index) => (
                <AnswerEditor
                  key={answer.id}
                  answer={answer}
                  index={index}
                  expanded={expandedAnswerId === answer.id}
                  nodes={story.nodes}
                  characters={story.characters}
                  variables={story.variables}
                  onToggle={() => setExpandedAnswerId((current) => current === answer.id ? null : answer.id)}
                  onChange={(nextAnswer) => onUpdateAnswer(answer.id, nextAnswer)}
                  onRemove={() => onRemoveAnswer(answer.id)}
                  onContinue={() => onContinueAnswer(answer.id)}
                  onLocateTarget={() => onLocateNode(answer.targetNodeId)}
                />
              ))}
              {!node.answers.length ? (
                <div className="interactive-empty-card">
                  <MessageSquareText size={22} />
                  <strong>还没有给玩家回答</strong>
                  <p>先写问题，再添加至少两个有意义的回答。</p>
                  <Button type="button" size="sm" variant="secondary" onClick={() => {
                    const answer = onAddAnswer();
                    if (answer?.id) setExpandedAnswerId(answer.id);
                  }}><Plus size={14} /> 添加第一个回答</Button>
                </div>
              ) : null}
            </div>
          </InspectorSection>
        )}
      </div>
    </>
  );
}

function CharacterInspector({ character, story, onUpdate, onDelete, onClose }) {
  return (
    <>
      <div className="interactive-inspector__titlebar">
        <div>
          <span>人物档案</span>
          <h2>{character.name || "未命名人物"}</h2>
        </div>
        <div className="interactive-inspector__title-actions">
          <IconButton type="button" size="sm" label="删除人物" className="interactive-icon-danger" onClick={onDelete}><Trash2 size={15} /></IconButton>
          <IconButton type="button" size="sm" label="关闭编辑器" onClick={onClose}><X size={16} /></IconButton>
        </div>
      </div>
      <div className="interactive-inspector__scroll">
        <InspectorSection title="人物身份" icon={Users}>
          <div className="interactive-character-identity">
            <label className="interactive-color-input" title="人物识别色">
              <input type="color" value={character.color || "#f97316"} onChange={(event) => onUpdate({ color: event.target.value })} />
              <span style={{ backgroundColor: character.color }} />
            </label>
            <Field label="姓名">
              <input value={character.name || ""} placeholder="人物姓名" onChange={(event) => onUpdate({ name: event.target.value })} />
            </Field>
          </div>
          <Field label="一句话定位">
            <input value={character.tagline || ""} placeholder="例如：表面冷静、一直在暗中调查真相的室友" onChange={(event) => onUpdate({ tagline: event.target.value })} />
          </Field>
          <Field label="性格">
            <textarea rows={4} value={character.personality || ""} placeholder="性格特征、习惯、面对压力时的反应……" onChange={(event) => onUpdate({ personality: event.target.value })} />
          </Field>
          <Field label="人物目标">
            <textarea rows={3} value={character.goal || ""} placeholder="她在这个故事中真正想完成什么？" onChange={(event) => onUpdate({ goal: event.target.value })} />
          </Field>
          <Field label="说话方式">
            <textarea rows={3} value={character.speechStyle || ""} placeholder="措辞、语速、口头禅、会回避什么……" onChange={(event) => onUpdate({ speechStyle: event.target.value })} />
          </Field>
          <Field label="秘密 / 隐藏动机">
            <textarea rows={4} value={character.secret || ""} placeholder="玩家一开始不应该知道的事实" onChange={(event) => onUpdate({ secret: event.target.value })} />
          </Field>
        </InspectorSection>

        <InspectorSection title="开场状态" icon={Variable} description="预演开始时，这个人物与主角的关系和个人剧情阶段。">
          <div className="interactive-two-column">
            <Field label="初始关系值">
              <input
                type="number"
                value={story.initialState.relations?.[character.id] ?? 0}
                onChange={(event) => onUpdate({ initialRelation: Number(event.target.value) })}
              />
            </Field>
            <Field label="初始人物阶段">
              <input
                value={story.initialState.stages?.[character.id] ?? ""}
                placeholder="例如：陌生"
                onChange={(event) => onUpdate({ initialStage: event.target.value })}
              />
            </Field>
          </div>
        </InspectorSection>
      </div>
    </>
  );
}

function StorySettingsInspector({ story, onUpdateStory, onUpdateChapter, onRemoveChapter, onAddFlag, onUpdateFlag, onRemoveFlag, onClose }) {
  return (
    <>
      <div className="interactive-inspector__titlebar">
        <div><span>项目级设置</span><h2>剧情与状态</h2></div>
        <IconButton type="button" size="sm" label="关闭设置" onClick={onClose}><X size={16} /></IconButton>
      </div>
      <div className="interactive-inspector__scroll">
        <InspectorSection title="故事信息" icon={BookOpen}>
          <Field label="互动游戏名称">
            <input value={story.title || ""} placeholder="互动游戏" onChange={(event) => onUpdateStory({ title: event.target.value })} />
          </Field>
          <Field label="创作说明">
            <textarea rows={5} value={story.description || ""} placeholder="主题、核心冲突、希望玩家获得的体验……" onChange={(event) => onUpdateStory({ description: event.target.value })} />
          </Field>
        </InspectorSection>

        <InspectorSection title="章节" icon={PanelLeft} description="章节只负责整理长篇剧情，不改变分支规则。">
          <div className="interactive-settings-list">
            {story.chapters.map((chapter, index) => {
              const nodeCount = story.nodes.filter((node) => node.chapterId === chapter.id).length;
              return (
                <div key={chapter.id} className="interactive-settings-row">
                  <span>{index + 1}</span>
                  <input value={chapter.title} onChange={(event) => onUpdateChapter(chapter.id, { title: event.target.value })} />
                  <small>{nodeCount} 段</small>
                  <IconButton type="button" size="xs" label="删除章节" disabled={story.chapters.length <= 1} onClick={() => onRemoveChapter(chapter.id)}><Trash2 size={13} /></IconButton>
                </div>
              );
            })}
          </div>
        </InspectorSection>

        <InspectorSection
          title="剧情标记"
          icon={Variable}
          description="用“是否成立”的事实控制回答是否出现，例如：已经拿到钥匙。"
          actions={<Button type="button" size="xs" variant="secondary" onClick={onAddFlag}><Plus size={12} /> 标记</Button>}
        >
          <div className="interactive-flag-list">
            {story.variables.map((variable) => (
              <div key={variable.id || variable.key} className="interactive-flag-row">
                <div className="interactive-flag-row__main">
                  <input value={variable.name || ""} placeholder="标记名称" onChange={(event) => onUpdateFlag(variable.id, { name: event.target.value })} />
                  <code>{variable.key}</code>
                </div>
                <label className="interactive-switch-label">
                  <input type="checkbox" checked={Boolean(story.initialState.flags?.[variable.key])} onChange={(event) => onUpdateFlag(variable.id, { defaultValue: event.target.checked })} />
                  <span>开场已成立</span>
                </label>
                <IconButton type="button" size="xs" label="删除标记" onClick={() => onRemoveFlag(variable.id)}><Trash2 size={13} /></IconButton>
              </div>
            ))}
            {!story.variables.length ? <p className="interactive-rule-empty">还没有剧情标记。需要条件分支时再添加即可。</p> : null}
          </div>
        </InspectorSection>
      </div>
    </>
  );
}

export default function InteractiveStoryWorkbench() {
  const { projectId } = useParams();
  const toast = useToast();
  const flowInstanceRef = useRef(null);
  const revisionRef = useRef(0);
  const activeProjectIdRef = useRef(projectId);
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [activePanel, setActivePanel] = useState("story");
  const [selection, setSelection] = useState({ type: "node", id: null });
  const [searchText, setSearchText] = useState("");
  const [previewStartNodeId, setPreviewStartNodeId] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [inspectorOpen, setInspectorOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    activeProjectIdRef.current = projectId;
    setLoading(true);
    setLoadError("");
    setDirty(false);
    setSidebarCollapsed(true);
    setInspectorOpen(false);
    fetchInteractiveStoryState(projectId)
      .then((data) => {
        if (cancelled) return;
        const next = ensureWorkspaceState(data, projectId);
        const needsInitialSave = needsWorkspaceMigration(data);
        setStory(next);
        setSelection({ type: "node", id: next.entryNodeId || next.nodes[0]?.id || null });
        revisionRef.current = 0;
        setDirty(needsInitialSave);
      })
      .catch((error) => {
        if (cancelled) return;
        const fallback = ensureWorkspaceState({}, projectId);
        setStory(fallback);
        setSelection({ type: "node", id: fallback.entryNodeId });
        revisionRef.current = 0;
        setLoadError(`暂时无法读取已保存内容：${error.message}`);
        toast.error("互动游戏读取失败，已打开本地空白稿");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [projectId]);

  const mutateStory = useCallback((updater) => {
    setStory((current) => {
      if (!current) return current;
      return typeof updater === "function" ? updater(current) : { ...current, ...updater };
    });
    revisionRef.current += 1;
    setDirty(true);
  }, []);

  const saveStory = useCallback(async () => {
    if (!story || saving) return;
    if (loadError) {
      toast.warning("读取失败时不能覆盖服务器内容，请先恢复后端并刷新页面");
      return;
    }
    const snapshotRevision = revisionRef.current;
    const snapshotProjectId = projectId;
    const snapshot = normalizeStoryState(story, projectId);
    setSaving(true);
    try {
      const saved = await saveInteractiveStoryState(projectId, snapshot);
      if (String(activeProjectIdRef.current) !== String(snapshotProjectId)) return;
      if (revisionRef.current === snapshotRevision) {
        setStory(ensureWorkspaceState(saved, projectId));
        setDirty(false);
      } else {
        setDirty(true);
        toast.info("请求期间又有新修改，旧版本已保存；请再保存一次最新内容");
        return;
      }
      setLoadError("");
      toast.success("互动游戏已保存");
    } catch (error) {
      toast.error(`保存失败：${error.message}`);
    } finally {
      setSaving(false);
    }
  }, [loadError, projectId, saving, story, toast]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "s") {
        event.preventDefault();
        saveStory();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [saveStory]);

  useEffect(() => {
    const warnUnsaved = (event) => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnUnsaved);
    return () => window.removeEventListener("beforeunload", warnUnsaved);
  }, [dirty]);

  useEffect(() => {
    const closeTransientPanels = (event) => {
      if (event.key !== "Escape" || previewStartNodeId) return;
      setSidebarCollapsed(true);
      setInspectorOpen(false);
    };
    window.addEventListener("keydown", closeTransientPanels);
    return () => window.removeEventListener("keydown", closeTransientPanels);
  }, [previewStartNodeId]);

  const selectedNode = story?.nodes.find((node) => selection.type === "node" && node.id === selection.id) || null;
  const selectedCharacter = story?.characters.find((character) => selection.type === "character" && character.id === selection.id) || null;

  const openSidebarPanel = useCallback((panel) => {
    if (!sidebarCollapsed && activePanel === panel) {
      setSidebarCollapsed(true);
      return;
    }
    setActivePanel(panel);
    setInspectorOpen(false);
    setSidebarCollapsed(false);
  }, [activePanel, sidebarCollapsed]);

  const openStorySettings = useCallback(() => {
    if (inspectorOpen && selection.type === "state") {
      setInspectorOpen(false);
      return;
    }
    setSelection({ type: "state", id: null });
    setSidebarCollapsed(true);
    setInspectorOpen(true);
  }, [inspectorOpen, selection.type]);

  const selectNode = useCallback((nodeId, center = false) => {
    if (!nodeId) return;
    setSelection({ type: "node", id: nodeId });
    setActivePanel("story");
    setSidebarCollapsed(true);
    setInspectorOpen(true);
    if (center) {
      const node = story?.nodes.find((item) => item.id === nodeId);
      if (node && flowInstanceRef.current) {
        flowInstanceRef.current.setCenter(node.position.x + 160, node.position.y + 110, { zoom: 0.95, duration: 320 });
      }
    }
  }, [story]);

  const selectCharacter = useCallback((characterId) => {
    setSelection({ type: "character", id: characterId });
    setActivePanel("characters");
    setSidebarCollapsed(true);
    setInspectorOpen(true);
  }, []);

  const updateNode = useCallback((nodeId, patch) => {
    mutateStory((current) => ({
      ...current,
      nodes: current.nodes.map((node) => node.id === nodeId ? { ...node, ...patch } : node),
    }));
  }, [mutateStory]);

  const createNode = useCallback((nodeType = "story", options = {}) => {
    if (!story) return null;
    const anchor = options.parentNode || selectedNode;
    const fallbackIndex = story.nodes.length;
    const position = options.position || (anchor
      ? { x: anchor.position.x + 360, y: anchor.position.y + 120 }
      : { x: 120 + (fallbackIndex % 3) * 360, y: 120 + Math.floor(fallbackIndex / 3) * 360 });
    const node = createStoryNode({
      nodeType,
      title: options.title || (nodeType === "ending" ? "新的结局" : "新的剧情"),
      chapterId: options.chapterId || anchor?.chapterId || story.chapters[0]?.id,
      trackType: options.trackType || (nodeType === "ending" ? "ending_check" : anchor?.trackType || "main"),
      participants: options.participants || anchor?.participants || [],
      position,
      endingType: nodeType === "ending" ? "normal" : null,
    });
    mutateStory((current) => ({ ...current, nodes: [...current.nodes, node] }));
    setSelection({ type: "node", id: node.id });
    setActivePanel("story");
    setSidebarCollapsed(true);
    setInspectorOpen(true);
    requestAnimationFrame(() => {
      flowInstanceRef.current?.setCenter(position.x + 160, position.y + 110, { zoom: 0.95, duration: 320 });
    });
    return node;
  }, [mutateStory, selectedNode, story]);

  const deleteNode = useCallback(async (nodeId) => {
    if (!story || story.nodes.length <= 1) {
      toast.warning("故事至少需要保留一个节点");
      return;
    }
    const node = story.nodes.find((item) => item.id === nodeId);
    if (!(await appConfirm(`确定删除“${node?.title || "这段剧情"}”吗？指向它的回答会变成未续写。`))) return;
    const remaining = story.nodes.filter((item) => item.id !== nodeId);
    const nextEntryId = story.entryNodeId === nodeId ? remaining[0]?.id : story.entryNodeId;
    mutateStory((current) => ({
      ...current,
      entryNodeId: nextEntryId,
      startNodeId: nextEntryId,
      nodes: current.nodes
        .filter((item) => item.id !== nodeId)
        .map((item) => ({
          ...item,
          answers: item.answers.map((answer) => answer.targetNodeId === nodeId ? { ...answer, targetNodeId: null } : answer),
        })),
    }));
    setSelection({ type: "node", id: remaining[0]?.id || null });
  }, [mutateStory, story, toast]);

  const setEntryNode = useCallback((nodeId) => {
    mutateStory((current) => ({ ...current, entryNodeId: nodeId, startNodeId: nodeId }));
    toast.success("已设为故事开场");
  }, [mutateStory, toast]);

  const changeNodeType = useCallback(async (nodeId, nodeType) => {
    const node = story.nodes.find((item) => item.id === nodeId);
    if (!node || node.nodeType === nodeType) return;
    if (nodeType === "ending" && node.answers.length && !(await appConfirm("改成结局后，这个节点的回答和后续连接会被移除。继续吗？"))) return;
    updateNode(nodeId, nodeType === "ending"
      ? {
          nodeType,
          question: "",
          answers: [],
          endingType: "normal",
          trackType: node.trackType === "lock_be" ? "lock_be" : "ending_check",
        }
      : { nodeType, endingType: null });
  }, [story, updateNode]);

  const addAnswer = useCallback((nodeId) => {
    const answer = createStoryAnswer({ text: `回答 ${(story.nodes.find((node) => node.id === nodeId)?.answers.length || 0) + 1}`, impactType: "local" });
    mutateStory((current) => ({
      ...current,
      nodes: current.nodes.map((node) => node.id === nodeId ? { ...node, answers: [...node.answers, answer] } : node),
    }));
    return answer;
  }, [mutateStory, story]);

  const updateAnswer = useCallback((nodeId, answerId, nextAnswer) => {
    mutateStory((current) => ({
      ...current,
      nodes: current.nodes.map((node) => node.id === nodeId
        ? { ...node, answers: node.answers.map((answer) => answer.id === answerId ? nextAnswer : answer) }
        : node),
    }));
  }, [mutateStory]);

  const removeAnswer = useCallback((nodeId, answerId) => {
    mutateStory((current) => ({
      ...current,
      nodes: current.nodes.map((node) => node.id === nodeId
        ? { ...node, answers: node.answers.filter((answer) => answer.id !== answerId) }
        : node),
    }));
  }, [mutateStory]);

  const continueAnswer = useCallback((nodeId, answerId) => {
    const parent = story.nodes.find((node) => node.id === nodeId);
    const answerIndex = parent?.answers.findIndex((answer) => answer.id === answerId) ?? -1;
    const answer = parent?.answers[answerIndex];
    if (!parent || !answer) return;
    if (answer.targetNodeId) {
      selectNode(answer.targetNodeId, true);
      return;
    }
    const centerIndex = Math.max(0, (parent.answers.length - 1) / 2);
    const xOffset = (answerIndex - centerIndex) * 350;
    const child = createNode("story", {
      parentNode: parent,
      title: answer.text || "后续剧情",
      chapterId: parent.chapterId,
      trackType: parent.trackType,
      participants: parent.participants,
      position: { x: parent.position.x + xOffset, y: parent.position.y + 430 },
    });
    if (child) updateAnswer(nodeId, answerId, { ...answer, targetNodeId: child.id });
  }, [createNode, selectNode, story, updateAnswer]);

  const organizeStoryTree = useCallback(async () => {
    if (!story || story.nodes.length < 2) return;
    if (!(await appConfirm("按剧情关系重新整理全部节点？这会覆盖当前手工位置，但不会立即保存。"))) return;
    const nodes = layoutStoryGraph(story.nodes, story.entryNodeId);
    mutateStory((current) => ({ ...current, nodes }));
    requestAnimationFrame(() => {
      flowInstanceRef.current?.fitView({ padding: 0.1, minZoom: 0.06, maxZoom: 1, duration: 420 });
    });
    toast.success("已按剧情层级整理，可继续拖动微调");
  }, [mutateStory, story, toast]);

  const addCharacter = useCallback(() => {
    const character = createStoryCharacter({}, story.characters.length);
    mutateStory((current) => ({
      ...current,
      characters: [...current.characters, character],
      initialState: {
        ...current.initialState,
        relations: { ...current.initialState.relations, [character.id]: 0 },
        stages: { ...current.initialState.stages, [character.id]: "" },
      },
    }));
    selectCharacter(character.id);
  }, [mutateStory, selectCharacter, story]);

  const updateCharacter = useCallback((characterId, patch) => {
    mutateStory((current) => {
      const profilePatch = { ...patch };
      delete profilePatch.initialRelation;
      delete profilePatch.initialStage;
      const next = {
        ...current,
        characters: current.characters.map((character) => character.id === characterId ? { ...character, ...profilePatch } : character),
      };
      if (Object.prototype.hasOwnProperty.call(patch, "initialRelation")) {
        next.initialState = {
          ...next.initialState,
          relations: { ...next.initialState.relations, [characterId]: patch.initialRelation },
        };
      }
      if (Object.prototype.hasOwnProperty.call(patch, "initialStage")) {
        next.initialState = {
          ...next.initialState,
          stages: { ...next.initialState.stages, [characterId]: patch.initialStage },
        };
      }
      return next;
    });
  }, [mutateStory]);

  const deleteCharacter = useCallback(async (characterId) => {
    const character = story.characters.find((item) => item.id === characterId);
    if (!(await appConfirm(`确定删除人物“${character?.name || "未命名人物"}”吗？相关参与人和人物状态条件也会被清理。`))) return;
    mutateStory((current) => {
      const relations = { ...current.initialState.relations };
      const stages = { ...current.initialState.stages };
      delete relations[characterId];
      delete stages[characterId];
      return {
        ...current,
        characters: current.characters.filter((item) => item.id !== characterId),
        initialState: { ...current.initialState, relations, stages },
        nodes: current.nodes.map((node) => ({
          ...node,
          participants: node.participants.filter((id) => id !== characterId),
          answers: node.answers.map((answer) => ({
            ...answer,
            focusCharacterId: answer.focusCharacterId === characterId ? null : answer.focusCharacterId,
            conditions: answer.conditions.filter((condition) => !(["relation", "stage"].includes(condition.scope) && condition.key === characterId)),
            effects: answer.effects.filter((effect) => !(["relation", "stage"].includes(effect.scope) && effect.key === characterId)),
          })),
        })),
      };
    });
    setSelection({ type: "state", id: null });
    setActivePanel("characters");
  }, [mutateStory, story]);

  const addChapter = useCallback(async () => {
    const title = (await appPrompt("章节名称", `第 ${story.chapters.length + 1} 章`));
    if (!title?.trim()) return;
    const chapter = { id: generateStoryId("chapter"), title: title.trim(), order: story.chapters.length };
    mutateStory((current) => ({ ...current, chapters: [...current.chapters, chapter] }));
  }, [mutateStory, story]);

  const removeChapter = useCallback(async (chapterId) => {
    if (story.chapters.length <= 1) return;
    const chapter = story.chapters.find((item) => item.id === chapterId);
    if (!(await appConfirm(`删除章节“${chapter?.title}”？其中的剧情会移动到第一章。`))) return;
    const fallbackId = story.chapters.find((item) => item.id !== chapterId)?.id;
    mutateStory((current) => ({
      ...current,
      chapters: current.chapters.filter((item) => item.id !== chapterId),
      nodes: current.nodes.map((node) => node.chapterId === chapterId ? { ...node, chapterId: fallbackId } : node),
    }));
  }, [mutateStory, story]);

  const addFlag = useCallback(() => {
    const count = story.variables.length + 1;
    const id = generateStoryId("flag");
    const variable = {
      id,
      key: `flag_${id.replace(/[^a-zA-Z0-9]/g, "").slice(-10)}`,
      name: `剧情标记 ${count}`,
      type: "flag",
      defaultValue: false,
    };
    mutateStory((current) => ({
      ...current,
      variables: [...current.variables, variable],
      initialState: { ...current.initialState, flags: { ...current.initialState.flags, [variable.key]: false } },
    }));
  }, [mutateStory, story]);

  const updateFlag = useCallback((flagId, patch) => {
    mutateStory((current) => {
      const variable = current.variables.find((item) => item.id === flagId);
      if (!variable) return current;
      const nextKey = Object.prototype.hasOwnProperty.call(patch, "key") ? patch.key : variable.key;
      const flags = { ...current.initialState.flags };
      if (nextKey !== variable.key) {
        flags[nextKey] = flags[variable.key] ?? false;
        delete flags[variable.key];
      }
      if (Object.prototype.hasOwnProperty.call(patch, "defaultValue")) flags[nextKey] = patch.defaultValue;
      return {
        ...current,
        variables: current.variables.map((item) => item.id === flagId ? { ...item, ...patch, key: nextKey } : item),
        initialState: { ...current.initialState, flags },
        nodes: nextKey !== variable.key && variable.key
          ? current.nodes.map((node) => ({
              ...node,
              answers: node.answers.map((answer) => ({
                ...answer,
                conditions: answer.conditions.map((condition) => condition.scope === "flag" && condition.key === variable.key
                  ? { ...condition, key: nextKey }
                  : condition),
                effects: answer.effects.map((effect) => effect.scope === "flag" && effect.key === variable.key
                  ? { ...effect, key: nextKey }
                  : effect),
              })),
            }))
          : current.nodes,
      };
    });
  }, [mutateStory]);

  const removeFlag = useCallback((flagId) => {
    mutateStory((current) => {
      const variable = current.variables.find((item) => item.id === flagId);
      const flags = { ...current.initialState.flags };
      if (variable?.key) delete flags[variable.key];
      return {
        ...current,
        variables: current.variables.filter((item) => item.id !== flagId),
        initialState: { ...current.initialState, flags },
        nodes: variable?.key
          ? current.nodes.map((node) => ({
              ...node,
              answers: node.answers.map((answer) => ({
                ...answer,
                conditions: answer.conditions.filter((condition) => !(condition.scope === "flag" && condition.key === variable.key)),
                effects: answer.effects.filter((effect) => !(effect.scope === "flag" && effect.key === variable.key)),
              })),
            }))
          : current.nodes,
      };
    });
  }, [mutateStory]);

  const flowNodes = useMemo(() => {
    if (!story) return [];
    const chapterById = new Map(story.chapters.map((chapter) => [chapter.id, chapter]));
    const characterById = new Map(story.characters.map((character) => [character.id, character]));
    const nodeById = new Map(story.nodes.map((node) => [node.id, node]));
    return story.nodes.map((node) => ({
      id: node.id,
      type: "storyPlot",
      position: node.position,
      selected: selection.type === "node" && selection.id === node.id,
      data: {
        ...node,
        answers: node.answers.map((answer) => ({
          ...answer,
          targetNodeTitle: answer.targetNodeId
            ? nodeById.get(answer.targetNodeId)?.title || "未命名剧情"
            : "",
        })),
        isEntry: story.entryNodeId === node.id,
        chapterTitle: chapterById.get(node.chapterId)?.title,
        participantNames: node.participants.map((id) => characterById.get(id)?.name).filter(Boolean),
        endingTypeLabel: ENDING_OPTIONS.find((item) => item.value === node.endingType)?.label,
      },
    }));
  }, [selection, story]);

  const flowEdges = useMemo(() => deriveStoryEdges(story?.nodes || []).map((edge) => ({
    ...edge,
    type: "storyTrack",
    markerEnd: {
      type: MarkerType.ArrowClosed,
      color: edge.style?.stroke,
      width: 14,
      height: 14,
    },
  })), [story?.nodes]);

  const handleNodesChange = useCallback((changes) => {
    const positionChanges = changes.filter((change) => change.type === "position" && change.position);
    const selectedChange = changes.find((change) => change.type === "select" && change.selected);
    if (selectedChange) setSelection({ type: "node", id: selectedChange.id });
    if (!positionChanges.length) return;
    const positionById = new Map(positionChanges.map((change) => [change.id, change.position]));
    mutateStory((current) => ({
      ...current,
      nodes: current.nodes.map((node) => positionById.has(node.id) ? { ...node, position: positionById.get(node.id) } : node),
    }));
  }, [mutateStory]);

  const handleConnect = useCallback((connection) => {
    const sourceNodeId = connection.source;
    const targetNodeId = connection.target;
    const answerId = readStoryAnswerIdFromHandle(connection.sourceHandle);
    if (!sourceNodeId || !targetNodeId || !answerId) return;

    mutateStory((current) => {
      if (!current.nodes.some((node) => node.id === targetNodeId)) return current;
      let answerFound = false;
      const nodes = current.nodes.map((node) => {
        if (node.id !== sourceNodeId) return node;
        const answers = node.answers.map((answer) => {
          if (answer.id !== answerId) return answer;
          answerFound = true;
          return { ...answer, targetNodeId };
        });
        return answerFound ? { ...node, answers } : node;
      });
      return answerFound ? { ...current, nodes } : current;
    });
  }, [mutateStory]);

  const isValidStoryConnection = useCallback((connection) => {
    const sourceNodeId = connection.source;
    const targetNodeId = connection.target;
    const answerId = readStoryAnswerIdFromHandle(connection.sourceHandle);
    if (!story || !sourceNodeId || !targetNodeId || !answerId) return false;
    const sourceNode = story.nodes.find((node) => node.id === sourceNodeId);
    return Boolean(
      sourceNode?.answers.some((answer) => answer.id === answerId)
      && story.nodes.some((node) => node.id === targetNodeId),
    );
  }, [story]);

  const groupedNodes = useMemo(() => {
    if (!story) return [];
    const query = searchText.trim().toLowerCase();
    return [...story.chapters]
      .sort((a, b) => a.order - b.order)
      .map((chapter) => ({
        chapter,
        nodes: story.nodes.filter((node) => node.chapterId === chapter.id && (!query || `${node.title} ${node.content} ${node.question}`.toLowerCase().includes(query))),
      }));
  }, [searchText, story]);

  if (loading || !story) {
    return (
      <div className="interactive-story-loading">
        <Workflow size={28} />
        <strong>正在打开互动游戏</strong>
        <span>加载人物、剧情节点与分支状态……</span>
      </div>
    );
  }

  return (
    <div className="interactive-story-page">
      <header className="interactive-story-toolbar">
        <div className="interactive-story-toolbar__actions">
          <button type="button" className={`interactive-toolbar-button ${!sidebarCollapsed && activePanel === "story" ? "is-active" : ""}`} onClick={() => openSidebarPanel("story")} aria-label="打开剧情目录"><PanelLeft size={14} /><span>剧情树</span></button>
          <button type="button" className={`interactive-toolbar-button interactive-mobile-secondary ${!sidebarCollapsed && activePanel === "characters" ? "is-active" : ""}`} onClick={() => openSidebarPanel("characters")} aria-label="打开人物档案"><Users size={14} /><span>人物</span></button>
          <button type="button" className={`interactive-toolbar-button ${inspectorOpen && selection.type === "state" ? "is-active" : ""}`} onClick={openStorySettings} aria-label="打开剧情设置"><Settings2 size={14} /><span>设置</span></button>
          <span className="interactive-toolbar-divider" aria-hidden="true" />
          <button type="button" className="interactive-toolbar-button" onClick={() => createNode("story")}><Plus size={14} /><span>剧情</span></button>
          <button type="button" className="interactive-toolbar-button interactive-mobile-secondary" onClick={() => createNode("ending")}><Flag size={14} /><span>结局</span></button>
          <button type="button" className="interactive-toolbar-button interactive-mobile-secondary" onClick={organizeStoryTree} disabled={story.nodes.length < 2} aria-label="按剧情关系整理画布"><Network size={14} /><span>整理</span></button>
          <span className="interactive-toolbar-divider" aria-hidden="true" />
          <button type="button" className="interactive-toolbar-button" onClick={() => setPreviewStartNodeId(story.entryNodeId)} disabled={!story.nodes.length}><Play size={14} /><span>预演</span></button>
        </div>
        <div className="interactive-story-toolbar__identity">
          <div>
            <h1>{story.title || "互动游戏"}</h1>
          </div>
          {dirty ? <span className="interactive-save-state is-dirty"><i />未保存</span> : null}
          <Button type="button" size="sm" variant="primary" loading={saving} disabled={!dirty || Boolean(loadError)} onClick={saveStory}>
            <Save size={14} /> 保存
          </Button>
        </div>
      </header>

      {loadError ? (
        <div className="interactive-story-warning" role="alert">
          <CircleAlert size={15} />
          <span>{loadError}。为避免覆盖服务器旧稿，当前只允许临时编辑，恢复服务并刷新页面后才能保存。</span>
          <strong>暂不可保存</strong>
        </div>
      ) : null}

      <div className={`interactive-story-workspace ${!sidebarCollapsed ? "has-left-drawer" : ""} ${inspectorOpen ? "has-right-drawer" : ""}`}>
        {!sidebarCollapsed ? (
        <aside className="interactive-story-sidebar" aria-label="剧情资料抽屉">
          <div className="interactive-story-sidebar__tabs" role="tablist" aria-label="剧情资料">
            {[
              { id: "story", label: "剧情", icon: GitBranch },
              { id: "characters", label: "人物", icon: Users },
            ].map(({ id, label, icon: Icon }) => (
              <button
                type="button"
                key={id}
                role="tab"
                aria-selected={activePanel === id}
                className={activePanel === id ? "is-active" : ""}
                onClick={() => setActivePanel(id)}
              ><Icon size={14} /><span>{label}</span></button>
            ))}
            <button type="button" className="interactive-sidebar-close" onClick={() => setSidebarCollapsed(true)} aria-label="关闭剧情资料"><X size={15} /></button>
          </div>

          {activePanel === "story" ? (
            <div className="interactive-story-sidebar__content">
              <div className="interactive-sidebar-tools">
                <div className="interactive-search"><Search size={14} /><input value={searchText} placeholder="搜索剧情" onChange={(event) => setSearchText(event.target.value)} /></div>
                <IconButton type="button" size="sm" label="添加章节" onClick={addChapter}><Plus size={15} /></IconButton>
              </div>
              <div className="interactive-outline-list">
                {groupedNodes.map(({ chapter, nodes }) => (
                  <section key={chapter.id} className="interactive-outline-chapter">
                    <header><strong>{chapter.title}</strong><span>{nodes.length}</span></header>
                    {nodes.map((node) => {
                      const nodeTrack = STORY_TRACK_TYPES[node.trackType] || STORY_TRACK_TYPES.main;
                      return (
                        <button
                          type="button"
                          key={node.id}
                          className={selection.type === "node" && selection.id === node.id ? "is-active" : ""}
                          style={{ "--story-track-color": nodeTrack.color }}
                          onClick={() => selectNode(node.id, true)}
                        >
                          <span className={`interactive-outline-node-icon ${node.nodeType}`}>
                            {node.nodeType === "ending" ? <Flag size={13} /> : <MessageSquareText size={13} />}
                          </span>
                          <span>
                            <strong>{node.title || "未命名剧情"}</strong>
                            <small>{nodeTrack.shortLabel} · {node.nodeType === "ending" ? "结局" : `${node.answers.length} 个回答`}</small>
                          </span>
                          {story.entryNodeId === node.id ? <CircleDot size={12} className="interactive-entry-mark" /> : null}
                        </button>
                      );
                    })}
                    {!nodes.length ? <p>本章暂无匹配剧情</p> : null}
                  </section>
                ))}
              </div>
            </div>
          ) : null}

          {activePanel === "characters" ? (
            <div className="interactive-story-sidebar__content">
              <div className="interactive-sidebar-section-title"><strong>人物档案</strong><IconButton type="button" size="sm" label="添加人物" onClick={addCharacter}><UserPlus size={15} /></IconButton></div>
              <div className="interactive-character-list">
                {story.characters.map((character) => (
                  <button type="button" key={character.id} className={selection.type === "character" && selection.id === character.id ? "is-active" : ""} onClick={() => selectCharacter(character.id)}>
                    <span className="interactive-character-avatar" style={{ backgroundColor: character.color }}>{character.name?.slice(0, 1) || "人"}</span>
                    <span><strong>{character.name || "未命名人物"}</strong><small>{character.tagline || character.goal || "补充人物介绍"}</small></span>
                    <ChevronRight size={14} />
                  </button>
                ))}
                {!story.characters.length ? (
                  <div className="interactive-empty-sidebar"><Users size={24} /><strong>先建立人物</strong><p>人物目标与秘密会一直陪着编剧写作。</p><Button type="button" size="sm" variant="secondary" onClick={addCharacter}><UserPlus size={14} /> 添加人物</Button></div>
                ) : null}
              </div>
            </div>
          ) : null}

        </aside>
        ) : null}

        <main className="interactive-story-canvas" aria-label="互动游戏画布">
          <ReactFlow
            nodes={flowNodes}
            edges={flowEdges}
            nodeTypes={NODE_TYPES}
            edgeTypes={EDGE_TYPES}
            onNodesChange={handleNodesChange}
            onConnect={handleConnect}
            isValidConnection={isValidStoryConnection}
            onNodeClick={(_, node) => selectNode(node.id)}
            onPaneClick={() => {
              setSidebarCollapsed(true);
              setInspectorOpen(false);
            }}
            onInit={(instance) => { flowInstanceRef.current = instance; }}
            deleteKeyCode={null}
            nodesConnectable
            edgesReconnectable={false}
            connectionRadius={30}
            connectionLineStyle={{ stroke: "var(--color-accent-primary)", strokeWidth: 2 }}
            elementsSelectable
            panOnDrag={[0, 1, 2]}
            zoomOnScroll
            fitView
            fitViewOptions={{ padding: 0.12, minZoom: 0.06, maxZoom: 1 }}
            minZoom={0.06}
            maxZoom={1.6}
            onlyRenderVisibleElements
            proOptions={{ hideAttribution: true }}
          >
            <Background id="story-grid" variant="lines" gap={120} size={1} color="var(--color-border-secondary)" style={{ opacity: 0.16 }} />
            <Background id="story-dots" variant="dots" gap={16} size={1} color="var(--color-border-primary)" style={{ opacity: 0.28 }} />
            <Controls showInteractive={false} position="bottom-left" />
          </ReactFlow>
          <div className="interactive-canvas-legend" role="list" aria-label="剧情线路图例">
            {STORY_TRACK_TYPE_OPTIONS.map((item) => (
              <span role="listitem" key={item.value} style={{ "--story-track-color": item.color }}><i aria-hidden="true" />{item.label}</span>
            ))}
          </div>
          <div className="interactive-canvas-glow" aria-hidden="true" />
          <div className="interactive-canvas-vignette" aria-hidden="true" />
        </main>

        {inspectorOpen ? (
        <aside className="interactive-story-inspector" aria-label="剧情编辑抽屉">
          {selectedNode ? (
            <NodeInspector
              node={selectedNode}
              story={story}
              onUpdate={(patch) => updateNode(selectedNode.id, patch)}
              onDelete={() => deleteNode(selectedNode.id)}
              onSetEntry={() => setEntryNode(selectedNode.id)}
              onChangeType={(nodeType) => changeNodeType(selectedNode.id, nodeType)}
              onUpdateAnswer={(answerId, nextAnswer) => updateAnswer(selectedNode.id, answerId, nextAnswer)}
              onAddAnswer={() => addAnswer(selectedNode.id)}
              onRemoveAnswer={(answerId) => removeAnswer(selectedNode.id, answerId)}
              onContinueAnswer={(answerId) => continueAnswer(selectedNode.id, answerId)}
              onLocateNode={(nodeId) => selectNode(nodeId, true)}
              onSelectCharacter={selectCharacter}
              onPreview={() => setPreviewStartNodeId(selectedNode.id)}
              onClose={() => setInspectorOpen(false)}
            />
          ) : selectedCharacter ? (
            <CharacterInspector
              character={selectedCharacter}
              story={story}
              onUpdate={(patch) => updateCharacter(selectedCharacter.id, patch)}
              onDelete={() => deleteCharacter(selectedCharacter.id)}
              onClose={() => setInspectorOpen(false)}
            />
          ) : (
            <StorySettingsInspector
              story={story}
              onUpdateStory={(patch) => mutateStory((current) => ({ ...current, ...patch }))}
              onUpdateChapter={(chapterId, patch) => mutateStory((current) => ({ ...current, chapters: current.chapters.map((chapter) => chapter.id === chapterId ? { ...chapter, ...patch } : chapter) }))}
              onRemoveChapter={removeChapter}
              onAddFlag={addFlag}
              onUpdateFlag={updateFlag}
              onRemoveFlag={removeFlag}
              onClose={() => setInspectorOpen(false)}
            />
          )}
        </aside>
        ) : null}
      </div>

      {previewStartNodeId ? (
        <StoryPreviewModal story={story} startNodeId={previewStartNodeId} onClose={() => setPreviewStartNodeId(null)} />
      ) : null}
    </div>
  );
}
